import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
} from '@tanstack/react-router';
import { validatePhoneStatsSearch } from './routes/phone-stats-search';
import { AppLayout } from './routes/app-layout';
import { ChannelRoute } from './routes/channel';
import { HomeRoute } from './routes/home';
import { LoginPage } from './routes/login';

/*
  A RITKAN HASZNALT OLDALAK KESOBB TOLTODNEK LE (K2, 4. csomag).

  Az admin, az audit, a statisztika, a kereses, a hivas ablaka es tarsaik
  eddig minden indulaskor letoltodtek. Most csak megnyitaskor - az indulashoz
  csak a csatornalista es a beszelgetes kell.
*/
const rootRoute = createRootRoute({ component: () => <Outlet /> });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

/** Jelszo-visszaallitas e-mailben kapott linkkel - bejelentkezes nelkul erheto el. */
const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset',
  component: lazyRouteComponent(() => import('./routes/reset'), 'ResetPage'),
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search.token === 'string' ? { token: search.token } : {},
});

/**
 * A huddle SAJAT ablaka - a fo elrendezesen KIVUL.
 *
 * Nincs oldalsav, nincs csatornalista: csak a hivas. A media-kapcsolatot ez
 * az ablak tartja, ezert a fo ablak bezarasa nem szakitja meg.
 */
const huddleWindowRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/huddle/$channelId',
  component: lazyRouteComponent(() => import('./routes/huddle-window'), 'HuddleWindowPage'),
});

/**
 * A megosztott kijelzo zold kerete - az asztali app atlatszo ablakaban.
 *
 * A fo elrendezesen kivul all, es SEMMILYEN munkamenetet nem igenyel: ez a lap
 * csak egy keretet rajzol. Lasd `show_share_frame` a Rust oldalon.
 */
const shareFrameRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/megosztas-keret',
  component: lazyRouteComponent(() => import('./routes/share-frame'), 'ShareFramePage'),
});

/** Utvonal nelkuli elrendezes-route: minden bejelentkezett nezet szuloje. */
/*
  A VENDEG OLDALA (H2) - bejelentkezes nelkul, a fo elrendezesen kivul. A
  kulso fel a kapott linket nyitja meg, nevet ad, varakozik, majd belep.
*/
const guestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vendeg/$token',
  component: lazyRouteComponent(() => import('./routes/guest'), 'GuestPage'),
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppLayout,
});

const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: HomeRoute,
});

const channelRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/c/$channelId',
  component: ChannelRoute,
  /**
   * Permalink: ?m=<uzenet azonosito> - ide ugrik es kiemeli a nezet.
   * A kulcs ELHAGYHATO: ha a visszateresi tipusban kotelezo lenne, minden
   * csatorna-linknek meg kellene adnia.
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): { m?: string; canvas?: string; atirat?: string } => ({
    ...(typeof search.m === 'string' ? { m: search.m } : {}),
    // ?canvas=<azonosito> - a keresesi talalatbol ezt a vasznat nyitja meg.
    ...(typeof search.canvas === 'string' ? { canvas: search.canvas } : {}),
    // ?atirat=<huddle> - a hivas atiratat nyitja meg (a ProjectFlow linkje).
    ...(typeof search.atirat === 'string' ? { atirat: search.atirat } : {}),
  }),
});

/** A telefonos csatorna statisztikaja - a szurok a cimben elnek. */
const phoneStatsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/c/$channelId/statisztika',
  component: lazyRouteComponent(() => import('./routes/phone-stats'), 'PhoneStatsRoute'),
  validateSearch: validatePhoneStatsSearch,
});

const searchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/search',
  component: lazyRouteComponent(() => import('./routes/search'), 'SearchRoute'),
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search.q === 'string' ? { q: search.q } : {},
});

const savedRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/saved',
  component: lazyRouteComponent(() => import('./routes/saved'), 'SavedRoute'),
});

/** Munkatarsak nevjegyzeke (versenyaudit AD2). */
const peopleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/people',
  component: lazyRouteComponent(() => import('./routes/people'), 'PeopleRoute'),
});

const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin',
  component: lazyRouteComponent(() => import('./routes/admin'), 'AdminPage'),
  // A ful az URL-ben: F5 utan es linkkent is ugyanaz a ful nyilik.
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search.tab === 'string' ? { tab: search.tab } : {},
});

/** Audit - indokolt betekintes, lasd docs/18-audit-kereses.html. */
/** PISZKOZATOK, utemezett es elkuldott uzenetek egy helyen (U8). */
const draftsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/piszkozatok',
  component: lazyRouteComponent(() => import('./routes/drafts'), 'DraftsRoute'),
});

/** NAPTAR (H1): sajat, csatorna- es csoportnaptar - a forras a cimben el. */
const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/naptar',
  component: lazyRouteComponent(() => import('./routes/calendar'), 'CalendarRoute'),
  validateSearch: (search: Record<string, unknown>): { forras?: string } =>
    typeof search.forras === 'string' ? { forras: search.forras } : {},
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit',
  component: lazyRouteComponent(() => import('./routes/audit'), 'AuditRoute'),
});

/** Egy nyitott audit-ugy munkaterulete: kereses es betekintes. */
const auditCaseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit/ugy/$caseId',
  component: lazyRouteComponent(() => import('./routes/audit-case'), 'AuditCaseRoute'),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  resetRoute,
  huddleWindowRoute,
  shareFrameRoute,
  guestRoute,
  appRoute.addChildren([
    homeRoute,
    channelRoute,
    phoneStatsRoute,
    savedRoute,
    peopleRoute,
    searchRoute,
    adminRoute,
    calendarRoute,
    draftsRoute,
    auditRoute,
    auditCaseRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
